/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — VOICE AGENT REPORT  (VA-1 … VA-10)
   Responsive single-file report. Stamp KPIs (except 86'd Impact), Orders
   Placed drill, Conversion Rate drill, Top Items, Time Saved and Call
   Activity Log are wired to the real Voice Agent API via the
   redux/reports/voiceAgent Redux module (Actions → Saga → services/voiceAgentApi.ts
   → Reducer), matching the clickHouse / reservationReport pattern used by the
   other reports. Total Calls drill, Voice Revenue drill, Transfer Rate drill
   and 86'd Impact remain on the voiceAgentData.ts sample pool — out of scope
   for this pass (owned by another integration piece / no backend endpoint yet).
   ═══════════════════════════════════════════════════════════════════════════ */

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import { useDrillHistoryBack } from "../../hooks/useDrillHistoryBack";
import { useAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar from "../layout/TopBar";
import BottomNav from "../BottomNav";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useSharedReportDateFilter } from "../../redux/reportFilters/reportFiltersHooks";
import { useCurrencySymbol, getCurrencySymbol } from "../../utils/currency";
import { ReportSectionExportButton } from "./components/ReportExportButton";
import { rawNumber } from "../../utils/reportExport";
import "./reports.css";
import { T, useIsDesktop, Bone } from "./reportTokens";
import KpiTooltip from "../KpiTooltip";
import {
    deriveVoiceAgentData, resolveDateRange, VoiceAgentData, VACall, Delta, CallOutcome, EightySixEncounter,
    deltaPct, mockQualityForKey, dayLabel, hourLabel,
    fmtInt, fmtCompact, fmtMoney, fmtMoney2, fmtMoneyCompact, fmtPct,
    fmtHrsMin, fmtDuration, fmtCallTime,
} from "./voiceAgentData";
import type {
    VoiceAgentSummary, TopItemRow, TimeSavedResponse, VoiceOrderRow, PaginationBlock,
    ConversionFunnelResponse, CallActivityLogRow, CallTranscriptResponse, CallTranscriptOrderItem, CallVolumeByHourItem, CallVolumeByDayItem,
    OrdersByDayItem, DayBucketGranularity,
    OutcomeDistributionItem, RevenueKpiResponse, TopRevenueItemRow, TransfersResponse, TransferReasonsResponse,
} from "../../services/voiceAgentApi";
import { getVoiceAgentCallActivityLog, getVoiceAgentOrders } from "../../services/voiceAgentApi";
import {
    fetchVoiceAgentSummaryRequest, fetchVoiceAgentTopItemsRequest, fetchVoiceAgentTimeSavedRequest,
    fetchVoiceAgentOrdersByDayRequest, fetchVoiceAgentOrdersRequest, fetchVoiceAgentConversionFunnelRequest,
    fetchVoiceAgentCallActivityLogRequest, fetchVoiceAgentCallActivityLogOutcomesRequest,
    setCallActivityLogOutcomeFilter,
    fetchVoiceAgentCallTranscriptRequest, clearVoiceAgentCallTranscript,
    fetchVoiceAgentCallVolumeByHourRequest, fetchVoiceAgentCallVolumeByDayRequest,
    fetchVoiceAgentOutcomeDistributionRequest, fetchVoiceAgentRevenueKpiRequest,
    fetchVoiceAgentTopRevenueItemsRequest, fetchVoiceAgentTransfersRequest,
    fetchVoiceAgentTransferReasonsRequest,
} from "../../redux/reports/voiceAgent/voiceAgentActions";

/* Reference to either a full mock call (untouched drills still use the sample
   pool directly) or a real API call identified by its Twilio callSid — the
   transcript slide-over fetches the latter on demand. */
type CallRef = VACall | string;

/* ═══════════════════════════════════════════════════════════════════════════
   DATA HOOK — simulates async load so shimmers show; derives from mock pool
   ═══════════════════════════════════════════════════════════════════════════ */

function useVoiceAgentData(period: string) {
    const data = useMemo(() => deriveVoiceAgentData(period), [period]);
    return { data };
}

/* ═══════════════════════════════════════════════════════════════════════════
   REDUX-BACKED API HOOKS — Summary / Top Items / Time Saved (main dashboard),
   Orders By Day + Recent Orders (Orders drill), Conversion Funnel (drill),
   Call Activity Log + outcome options, Call Transcript (slide-over).
   Each hook dispatches its `fetch*Request` action into the
   redux/reports/voiceAgent module (Saga → services/voiceAgentApi.ts → Reducer)
   and reads the result back via useAppSelector — the same
   dispatch → saga → service → reducer → selector flow used by the
   clickHouse / reservationReport modules.
   ═══════════════════════════════════════════════════════════════════════════ */

interface VARangeArgs { locationId: string; from: string; to: string }

/* React StrictMode double-invokes every effect in development (mount →
   cleanup → mount) to surface side-effect bugs. That's harmless for a plain
   UI effect, but for one that dispatches a network-fetching action it means
   every Voice Agent API call fires twice in dev. This hook skips the
   duplicate second invocation for an unchanged dependency key — the guard
   lives in a ref, which survives StrictMode's synchronous unmount+remount of
   the *same* component instance, but is gone (so a real fetch happens
   again) on a genuine unmount, e.g. navigating away from a drill-in and
   back. `ready` gates the effect the same way an early-return guard clause
   would; the key is only recorded once `ready` is true, so a later flip
   from not-ready to ready with the same key still fetches. */
function useDispatchOnceForKey(run: () => void, ready: boolean, key: React.DependencyList) {
    const lastKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (!ready) return;
        const k = JSON.stringify(key);
        if (lastKeyRef.current === k) return;
        lastKeyRef.current = k;
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, ...key]);
}

/* Summary + Top Items + Time Saved — power the top 5 KPI stamps, the Top
   Items chart and the Estimated Time Saved card on the main dashboard. All
   three requests omit from/to for the "Today" date filter, same convention
   as revenue-kpi / top-revenue-items (see the corresponding getVoiceAgent*
   functions in services/voiceAgentApi.ts). */
function useVoiceAgentApiData({ locationId, from, to, period }: VARangeArgs & { period: string }) {
    const dispatch = useAppDispatch();
    const summary = useAppSelector((s) => s.voiceAgent.summary) as VoiceAgentSummary | null;
    const summaryLoading = useAppSelector((s) => s.voiceAgent.summaryLoading);
    const topItems = useAppSelector((s) => s.voiceAgent.topItems) as TopItemRow[];
    const topItemsLoading = useAppSelector((s) => s.voiceAgent.topItemsLoading);
    const timeSaved = useAppSelector((s) => s.voiceAgent.timeSaved) as TimeSavedResponse | null;
    const timeSavedLoading = useAppSelector((s) => s.voiceAgent.timeSavedLoading);
    const sendDates = period !== "Today";

    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentSummaryRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
        dispatch(fetchVoiceAgentTopItemsRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
        dispatch(fetchVoiceAgentTimeSavedRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, !!locationId, [locationId, from, to, sendDates]);

    // Redux's loading flags default to true (see voiceAgentReducer) so the
    // shimmer covers the gap between first paint and the mount effect above
    // actually firing its dispatch. If there's genuinely no locationId to
    // fetch for, the effect never dispatches — so without this override the
    // page would be stuck showing the shimmer forever instead of resolving.
    const loading = locationId ? (summaryLoading || topItemsLoading || timeSavedLoading) : false;
    return { summary, topItems, timeSaved, loading };
}

/* Orders By Day — Orders Placed drill chart. */
function useOrdersByDay({ locationId, from, to, period }: VARangeArgs & { period: string }, enabled: boolean) {
    const dispatch = useAppDispatch();
    const days = useAppSelector((s) => s.voiceAgent.ordersByDay) as OrdersByDayItem[];
    const granularity = useAppSelector((s) => s.voiceAgent.ordersByDayGranularity) as DayBucketGranularity;
    const loading = useAppSelector((s) => s.voiceAgent.ordersByDayLoading);
    // Omit from/to for "Today" — same convention as every sibling hook — so
    // the backend resolves the current business date itself (5AM cutover)
    // instead of receiving today's literal calendar date, which the backend
    // treats as an explicit (and, before the store's cutover hour, not-yet-
    // started) business day. Missing this was RSP-11-class bug: going
    // Today -> Yesterday -> ... -> back to Today sent an explicit date and
    // showed zeros.
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentOrdersByDayRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, enabled && !!locationId, [locationId, from, to, sendDates]);
    return { days, granularity, loading };
}

/* Recent Voice Orders — Orders Placed drill table, server-paginated. */
function useRecentVoiceOrders({ locationId, from, to, period }: VARangeArgs & { period: string }, enabled: boolean, page: number, limit: number) {
    const dispatch = useAppDispatch();
    const rows = useAppSelector((s) => s.voiceAgent.orders) as VoiceOrderRow[];
    const totalElements = useAppSelector((s) => s.voiceAgent.ordersTotalElements);
    const totalPages = useAppSelector((s) => s.voiceAgent.ordersTotalPages);
    const loading = useAppSelector((s) => s.voiceAgent.ordersLoading);
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentOrdersRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined, page, limit }));
    }, enabled && !!locationId, [locationId, from, to, sendDates, page, limit]);
    const pagination: PaginationBlock | null = rows.length || totalElements ? { page, limit, total: totalElements, totalPages } : null;
    return { rows, pagination, loading };
}

/* Conversion Funnel — Conversion Rate drill. */
function useConversionFunnel({ locationId, from, to, period }: VARangeArgs & { period: string }, enabled: boolean) {
    const dispatch = useAppDispatch();
    const funnel = useAppSelector((s) => s.voiceAgent.conversionFunnel) as ConversionFunnelResponse | null;
    const loading = useAppSelector((s) => s.voiceAgent.conversionFunnelLoading);
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentConversionFunnelRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, enabled && !!locationId, [locationId, from, to, sendDates]);
    return { funnel, loading };
}

/* Call Activity Log — main dashboard table, server-paginated + outcome-filtered.
   Omits from/to for the "Today" date filter, same convention as the other
   main-dashboard requests. */
function useCallActivityLog(
    { locationId, from, to, period }: VARangeArgs & { period: string },
    outcome: string,
    sort: { sortBy?: 'duration' | 'time' | 'revenue'; sortOrder?: 'asc' | 'desc' },
    page: number,
    limit: number,
) {
    const dispatch = useAppDispatch();
    const rows = useAppSelector((s) => s.voiceAgent.callActivityLog) as CallActivityLogRow[];
    const totalElements = useAppSelector((s) => s.voiceAgent.callActivityLogTotalElements);
    const totalPages = useAppSelector((s) => s.voiceAgent.callActivityLogTotalPages);
    const loading = useAppSelector((s) => s.voiceAgent.callActivityLogLoading);
    const sendDates = period !== "Today";
    const { sortBy, sortOrder } = sort;
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentCallActivityLogRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined, outcome: outcome === "all" ? undefined : outcome, sortBy, sortOrder, page, limit }));
    }, !!locationId, [locationId, from, to, sendDates, outcome, sortBy, sortOrder, page, limit]);
    const pagination: PaginationBlock | null = rows.length || totalElements ? { page, limit, total: totalElements, totalPages } : null;
    return { rows, pagination, loading };
}

/* Call Volume by Hour — main dashboard chart. */
function useCallVolumeByHour({ locationId, from, to, period }: VARangeArgs & { period: string }) {
    const dispatch = useAppDispatch();
    const hours = useAppSelector((s) => s.voiceAgent.callVolumeByHour) as CallVolumeByHourItem[];
    const loading = useAppSelector((s) => s.voiceAgent.callVolumeByHourLoading);
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentCallVolumeByHourRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, !!locationId, [locationId, from, to, sendDates]);
    return { hours, loading };
}

/* Call Volume by Day — Total Calls drill chart. */
function useCallVolumeByDay({ locationId, from, to, period }: VARangeArgs & { period: string }, enabled: boolean) {
    const dispatch = useAppDispatch();
    const days = useAppSelector((s) => s.voiceAgent.callVolumeByDay) as CallVolumeByDayItem[];
    const granularity = useAppSelector((s) => s.voiceAgent.callVolumeByDayGranularity) as DayBucketGranularity;
    const loading = useAppSelector((s) => s.voiceAgent.callVolumeByDayLoading);
    // Omit from/to for "Today" — see useOrdersByDay's comment for why.
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentCallVolumeByDayRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, enabled && !!locationId, [locationId, from, to, sendDates]);
    return { days, granularity, loading };
}

/* Outcome Distribution — Total Calls drill. Omits from/to for the "Today"
   date filter, same convention as the other drill-in requests. */
function useOutcomeDistribution({ locationId, from, to, period }: VARangeArgs & { period: string }) {
    const dispatch = useAppDispatch();
    const outcomes = useAppSelector((s) => s.voiceAgent.outcomeDistribution) as OutcomeDistributionItem[];
    const loading = useAppSelector((s) => s.voiceAgent.outcomeDistributionLoading);
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentOutcomeDistributionRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, !!locationId, [locationId, from, to, sendDates]);
    return { outcomes, loading };
}

/* Voice Revenue KPI — Voice Revenue drill (Total Revenue, Avg Order Value,
   Top Item by Revenue). Per the API contract, the "Today" date filter omits
   from/to entirely rather than sending a same-day range. */
function useRevenueKpi({ locationId, from, to, period }: VARangeArgs & { period: string }) {
    const dispatch = useAppDispatch();
    const revenueKpi = useAppSelector((s) => s.voiceAgent.revenueKpi) as RevenueKpiResponse | null;
    const loading = useAppSelector((s) => s.voiceAgent.revenueKpiLoading);
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentRevenueKpiRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, !!locationId, [locationId, sendDates, sendDates ? from : "", sendDates ? to : ""]);
    return { revenueKpi, loading };
}

/* Top Revenue Items — Voice Revenue drill list. Same "Today" from/to
   omission convention as revenue-kpi. */
function useTopRevenueItems({ locationId, from, to, period }: VARangeArgs & { period: string }) {
    const dispatch = useAppDispatch();
    const items = useAppSelector((s) => s.voiceAgent.topRevenueItems) as TopRevenueItemRow[];
    const loading = useAppSelector((s) => s.voiceAgent.topRevenueItemsLoading);
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentTopRevenueItemsRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, !!locationId, [locationId, sendDates, sendDates ? from : "", sendDates ? to : ""]);
    return { items, loading };
}

/* Transfers — Transfer Rate drill (Total Transfers, Transfer Rate, Transfer
   Reasons breakdown derived client-side from the raw transfer list, and the
   Recent Transfers table). Omits from/to for the "Today" date filter. */
function useTransfers({ locationId, from, to, period }: VARangeArgs & { period: string }, enabled: boolean) {
    const dispatch = useAppDispatch();
    const transfers = useAppSelector((s) => s.voiceAgent.transfers) as TransfersResponse | null;
    const loading = useAppSelector((s) => s.voiceAgent.transfersLoading);
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentTransfersRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, enabled && !!locationId, [locationId, from, to, sendDates]);
    return { transfers, loading };
}

/* Transfer Reasons — Transfer Rate drill mini-KPIs (Total Transfers, Transfer
   Rate) and the Transfer Reasons breakdown, pre-aggregated server-side.
   Omits from/to for the "Today" date filter. */
function useTransferReasons({ locationId, from, to, period }: VARangeArgs & { period: string }, enabled: boolean) {
    const dispatch = useAppDispatch();
    const transferReasons = useAppSelector((s) => s.voiceAgent.transferReasons) as TransferReasonsResponse | null;
    const loading = useAppSelector((s) => s.voiceAgent.transferReasonsLoading);
    const sendDates = period !== "Today";
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentTransferReasonsRequest({ locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined }));
    }, enabled && !!locationId, [locationId, from, to, sendDates]);
    return { transferReasons, loading };
}

/* Outcome filter dropdown options — not date-scoped, fetched once per location. */
function useCallActivityLogOutcomes(locationId: string) {
    const dispatch = useAppDispatch();
    const outcomes = useAppSelector((s) => s.voiceAgent.callActivityLogOutcomes) as string[];
    useDispatchOnceForKey(() => {
        dispatch(fetchVoiceAgentCallActivityLogOutcomesRequest(locationId));
    }, !!locationId, [locationId]);
    return outcomes;
}

/* Call Transcript — fetched on demand when a real (callSid) call ref is opened. */
function useCallTranscript(callSid: string | null) {
    const dispatch = useAppDispatch();
    const data = useAppSelector((s) => s.voiceAgent.callTranscript) as CallTranscriptResponse | null;
    const loading = useAppSelector((s) => s.voiceAgent.callTranscriptLoading);
    const error = useAppSelector((s) => s.voiceAgent.callTranscriptError) as string | null;
    // Unlike the other Voice Agent hooks, this one can't use the shared
    // useDispatchOnceForKey guard: the transcript slide-over never actually
    // unmounts on close (it just renders null), so that guard's ref would
    // keep remembering "already fetched callSid A" across a close+reopen of
    // the SAME call — the close already cleared the Redux state, so the
    // reopen would then skip the dispatch and get stuck showing the shimmer
    // forever. This local ref is reset to null whenever the panel closes
    // (callSid goes null), so reopening the same call always refetches,
    // while a StrictMode dev double-invoke (which re-runs with the same
    // callSid, no intervening null) is still deduped as before.
    const lastFetchedRef = useRef<string | null>(null);
    useEffect(() => {
        if (!callSid) {
            dispatch(clearVoiceAgentCallTranscript());
            lastFetchedRef.current = null;
            return;
        }
        if (lastFetchedRef.current === callSid) return;
        lastFetchedRef.current = callSid;
        dispatch(fetchVoiceAgentCallTranscriptRequest(callSid));
    }, [dispatch, callSid]);
    return { data, loading, error };
}

/* Generic outcome → pill styling for real (arbitrary, backend-defined) outcome strings. */
const REAL_OUTCOME_META: Record<string, { color: string; bg: string; label: string }> = {
    order_placed: { color: T.green, bg: T.greenSoft, label: "Order Placed" },
    order_completed: { color: T.green, bg: T.greenSoft, label: "Order Placed" },
    sms_order_placed: { color: T.green, bg: T.greenSoft, label: "SMS Order Placed" },
    sms_payment_pending: { color: T.orange, bg: T.orangeSoft, label: "Payment Pending" },
    payment_pending: { color: T.orange, bg: T.orangeSoft, label: "Payment Pending" },
    transferred: { color: T.orange, bg: T.orangeSoft, label: "Transferred" },
    inquiry: { color: T.blue, bg: T.blueSoft, label: "Inquiry" },
    inquiry_only: { color: T.blue, bg: T.blueSoft, label: "Inquiry" },
    hangup: { color: T.t3, bg: T.hover, label: "Hangup" },
    customer_hangup: { color: T.t3, bg: T.hover, label: "Hangup" },
    abandoned: { color: T.t3, bg: T.hover, label: "Abandoned" },
    bogus_call_timeout: { color: T.t3, bg: T.hover, label: "Timeout" },
    duration_timeout: { color: T.t3, bg: T.hover, label: "Timeout" },
};
function titleCaseOutcome(s: string) {
    return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function outcomeMeta(outcome: string | null) {
    if (!outcome) return { color: T.t3, bg: T.hover, label: "—" };
    return REAL_OUTCOME_META[outcome] || { color: T.t3, bg: T.hover, label: titleCaseOutcome(outcome) };
}
function OutcomePillReal({ outcome }: { outcome: string | null }) {
    const m = outcomeMeta(outcome);
    return (
        <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: m.color, background: m.bg, fontFamily: T.font, whiteSpace: "nowrap" }}>
            {m.label}
        </span>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SMALL PRESENTATION PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

function deltaColor(d: Delta) {
    if (d.tone === "neutral") return T.t3;
    return d.positive ? T.green : T.red;
}

const OUTCOME_PILL: Record<CallOutcome, { color: string; bg: string; label: string }> = {
    order_completed: { color: T.green, bg: T.greenSoft, label: "Order Placed" },
    transferred: { color: T.orange, bg: T.orangeSoft, label: "Transferred" },
    abandoned: { color: T.t3, bg: T.hover, label: "Abandoned" },
    inquiry_only: { color: T.blue, bg: T.blueSoft, label: "Inquiry" },
    hangup: { color: T.t3, bg: T.hover, label: "Hangup" },
};

function OutcomePill({ outcome }: { outcome: CallOutcome }) {
    const p = OUTCOME_PILL[outcome];
    return (
        <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: p.color, background: p.bg, fontFamily: T.font, whiteSpace: "nowrap" }}>
            {p.label}
        </span>
    );
}

/* KPI card — VA-1, VA-8 */
function KPICard({ id, label, value, delta, drillable, onTap, valueColor, compact, loading }: {
    id?: string; label: string; value: string; delta?: Delta;
    drillable?: boolean; onTap?: () => void; valueColor?: string; compact?: boolean; loading?: boolean;
}) {
    return (
        <div
            id={id}
            onClick={onTap}
            style={{
                position: "relative", background: T.card,
                border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`,
                borderRadius: 12, padding: compact ? "10px 11px" : "14px 16px",
                boxShadow: T.cardShadow, cursor: drillable ? "pointer" : "default", minWidth: 0,
            }}
        >
            <div style={{ fontSize: compact ? 9.5 : 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: T.t3, fontFamily: T.font }}>{label}</div>
            {loading ? (
                <Bone w={70} h={compact ? 18 : 28} r={4} mb={compact ? 0 : 4} style={{ marginTop: 5 }} />
            ) : (
                <>
                    <div style={{ fontSize: compact ? 18 : 24, fontWeight: 700, color: valueColor || T.t1, marginTop: 5, fontFamily: T.font, lineHeight: 1.1 }}>{value}</div>
                    {delta && <div style={{ fontSize: compact ? 9.5 : 12.5, fontWeight: 600, color: deltaColor(delta), marginTop: 4, fontFamily: T.font }}>{delta.text}</div>}
                </>
            )}
            {drillable && <span className="material-symbols-rounded" style={{ position: "absolute", top: compact ? 6 : 12, right: compact ? 6 : 12, fontSize: compact ? 13 : 16, color: T.accent, opacity: 0.4 }}>chevron_right</span>}
        </div>
    );
}

/* Compact mini-KPI used in drill-in summary rows */
function MiniKPI({ label, value, sub, valueColor, loading }: { label: string; value: string; sub?: string; valueColor?: string; loading?: boolean }) {
    return (
        <div style={{ flex: 1, minWidth: 130, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: "12px 14px", boxShadow: T.cardShadow }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase", color: T.t3, fontFamily: T.font }}>{label}</div>
            {loading ? <Bone w={80} h={20} r={4} style={{ marginTop: 4 }} /> : (
                <>
                    <div style={{ fontSize: 20, fontWeight: 700, color: valueColor || T.t1, marginTop: 4, fontFamily: T.font }}>{value}</div>
                    {sub && <div style={{ fontSize: 11.5, color: T.t2, marginTop: 3, fontFamily: T.font }}>{sub}</div>}
                </>
            )}
        </div>
    );
}

/* Card wrapper */
function SectionCard({ title, info, right, children, style }: {
    title?: string; info?: string; right?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties;
}) {
    return (
        <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 20, boxShadow: T.cardShadow, ...style }}>
            {(title || right) && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {title && <div style={{ fontSize: 14.5, fontWeight: 700, color: T.t1, fontFamily: T.font }}>{title}</div>}
                        {info && <KpiTooltip text={info} />}
                    </div>
                    {right}
                </div>
            )}
            {children}
        </div>
    );
}

function EmptyMsg({ text, minHeight = 140 }: { text: string; minHeight?: number }) {
    return <div style={{ minHeight, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: T.t3, fontSize: 13, fontFamily: T.font, padding: 16 }}>{text}</div>;
}

// Same box SectionCard renders, but with the title itself shimmered (a Bone)
// instead of real text — used for Call Activity Log's loading state so the
// heading doesn't render as static text while everything below it is still
// shimmering, unlike every other KPI/chart card on this page.
function CallActivityLogShimmer() {
    return (
        <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 20, boxShadow: T.cardShadow }}>
            <Bone w={160} h={14} r={4} mb={14} />
            <Bone h={200} r={8} />
        </div>
    );
}

/* Horizontal proportional bar row (top items, transfer reasons, outcomes, revenue items) */
function BarRow({ label, valueText, ratio, color = T.accent, sub }: { label: string; valueText: string; ratio: number; color?: string; sub?: string }) {
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5, gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.t1, fontFamily: T.font, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
                <span style={{ fontSize: 12, color: T.t2, fontFamily: T.font, whiteSpace: "nowrap" }}>{valueText}</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: T.hover, overflow: "hidden" }}>
                <div style={{ width: `${Math.max(2, Math.min(100, ratio * 100))}%`, height: "100%", borderRadius: 4, background: color }} />
            </div>
            {sub && <div style={{ fontSize: 11, color: T.t3, marginTop: 3, fontFamily: T.font }}>{sub}</div>}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CHARTS
   ═══════════════════════════════════════════════════════════════════════════ */

/* Call Volume by Hour — VA-2.1, real API data. */
function HourVolumeChart({ items, loading }: { items: CallVolumeByHourItem[]; loading: boolean }) {
    if (loading && !items.length) return <Bone h={250} r={8} />;
    if (!items.length) return <EmptyMsg text="No calls recorded for the selected period." minHeight={220} />;
    const bars = items.map((it) => ({ hour: it.hour, label: hourLabel(it.hour), calls: it.calls }));
    const peak = bars.reduce((a, b) => (b.calls > a.calls ? b : a));
    const avgCallsPerHour = +(bars.reduce((s, b) => s + b.calls, 0) / bars.length).toFixed(1);
    const sorted = [...bars].sort((a, b) => b.calls - a.calls);
    const top3 = new Set(sorted.slice(0, 3).map((b) => b.hour));
    return (
        <>
            <ResponsiveContainer width="100%" height={250}>
                <BarChart data={bars} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 10.5, fill: T.t3 }} interval="preserveStartEnd" axisLine={{ stroke: T.divider }} tickLine={false} />
                    <YAxis tick={{ fontSize: 10.5, fill: T.t3 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip cursor={{ fill: T.hover }} contentStyle={{ borderRadius: 8, border: `1px solid ${T.cardBorder}`, fontFamily: T.font, fontSize: 12 }}
                        formatter={(v: any) => [`${v} calls`, "Calls"]} />
                    <ReferenceLine y={avgCallsPerHour} stroke={T.t3} strokeDasharray="4 4" />
                    <Bar dataKey="calls" radius={[4, 4, 0, 0]} maxBarSize={40}>
                        {bars.map((b) => (
                            <Cell key={b.hour} fill={b.hour === peak.hour ? T.accent : top3.has(b.hour) ? `${T.accent}99` : `${T.accent}40`} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11.5, color: T.t3, marginTop: 8, fontFamily: T.font }}>
                Peak: {peak.label} — {peak.calls} calls · Avg: {avgCallsPerHour} calls/hr
            </div>
        </>
    );
}

/* Top Items Ordered via Voice — VA-3.1 (CSS horizontal bars), real API data. */
function TopItemsChart({ items, loading }: { items: TopItemRow[]; loading: boolean }) {
    if (loading && !items.length) return <Bone h={220} r={8} />;
    if (!items.length) return <EmptyMsg text="No voice orders recorded for the selected period." minHeight={220} />;
    const max = items[0].orderCount || 1;
    return (
        <div>
            {items.map((it) => (
                <BarRow key={it.itemName} label={`${it.rank}. ${it.itemName}`} valueText={`${it.orderCount} orders · ${fmtMoney(it.revenue)}`}
                    ratio={it.orderCount / max} color={it.rank === 1 ? T.accent : `${T.accent}80`}
                    sub={`${it.percentOfVoiceOrders.toFixed(1)}% of voice orders`} />
            ))}
        </div>
    );
}

/* Label for one days[] entry, branching on the bucket granularity the server
   chose for the requested range (see DayBucketGranularity in
   services/voiceAgentApi.ts): "day" shows the single business date as
   before; "week" shows the bucket's first–last business day; "month"/"year"
   show a compact month or year label using the bucket's first business day
   (its `date`), since a chart x-axis label has no room for a full range. */
function bucketLabel(item: { date: string; endDate?: string }, granularity: DayBucketGranularity): string {
    const start = new Date(item.date + "T00:00:00");
    switch (granularity) {
        case "week":
            return item.endDate ? `${dayLabel(item.date)} – ${dayLabel(item.endDate)}` : dayLabel(item.date);
        case "month":
            return start.toLocaleDateString("en-US", { month: "short", year: "numeric" });
        case "year":
            return start.toLocaleDateString("en-US", { year: "numeric" });
        case "day":
        default:
            return dayLabel(item.date);
    }
}

/* Section-card heading suffix for a by-day chart, e.g. "Call Volume by
   Week" once the range is long enough that the server switches granularity
   — otherwise the header would keep saying "by Day" for a chart whose bars
   are actually week/month/year buckets. */
const BUCKET_HEADING: Record<DayBucketGranularity, string> = { day: "Day", week: "Week", month: "Month", year: "Year" };
/* Generic by-day bar chart (calls / orders / revenue) with peak highlight */
/* Generic {label,value}[] bar chart with peak highlight — shared by the mock
   by-day charts (calls/revenue, untouched) and the real Orders By Day chart. */
function GenericByDayChart({ rows, unitLabel, isMoney, granularity = "day" }: { rows: { label: string; value: number }[]; unitLabel: string; isMoney: boolean; granularity?: DayBucketGranularity }) {
    if (rows.length <= 1) return null;
    const max = Math.max(...rows.map((r) => r.value));
    const avg = rows.reduce((s, r) => s + r.value, 0) / rows.length;
    const peakIdx = rows.findIndex((r) => r.value === max);
    const fmt = isMoney ? (v: number) => fmtMoney(v) : (v: number) => fmtInt(v);
    // Each bar is a bucket sum, not a per-day figure once granularity is
    // above "day" — the trailing "/bucket-unit" suffix on the average must
    // follow suit instead of always implying a daily rate.
    const avgSuffix = { day: "/day", week: "/wk", month: "/mo", year: "/yr" }[granularity];
    return (
        <>
            <ResponsiveContainer width="100%" height={230}>
                <BarChart data={rows} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: T.t3 }} interval="preserveStartEnd" axisLine={{ stroke: T.divider }} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} allowDecimals={false} width={isMoney ? 48 : 32} />
                    <Tooltip cursor={{ fill: T.hover }} contentStyle={{ borderRadius: 8, border: `1px solid ${T.cardBorder}`, fontFamily: T.font, fontSize: 12 }}
                        formatter={(v: any) => [fmt(v), unitLabel]} />
                    <ReferenceLine y={avg} stroke={T.t3} strokeDasharray="4 4" />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={46}>
                        {rows.map((_, i) => <Cell key={i} fill={i === peakIdx ? T.accent : `${T.accent}55`} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11.5, color: T.t3, marginTop: 8, fontFamily: T.font }}>
                Peak: {rows[peakIdx].label} — {fmt(max)} {unitLabel.toLowerCase()} · Avg: {fmt(Math.round(avg))}{isMoney ? avgSuffix : ` ${unitLabel.toLowerCase()}`}
            </div>
        </>
    );
}

function ByDayChart({ data, metric }: { data: VoiceAgentData; metric: "calls" | "orders" | "revenue" }) {
    const rows = data.byDay.map((d) => ({ label: d.label, value: d[metric] }));
    const unitLabel = metric === "calls" ? "Calls" : metric === "orders" ? "Orders" : "Revenue";
    return <GenericByDayChart rows={rows} unitLabel={unitLabel} isMoney={metric === "revenue"} />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   CALL ACTIVITY LOG  — VA-5.1  +  TRANSCRIPT SLIDE-OVER — VA-6.1
   ═══════════════════════════════════════════════════════════════════════════ */

/* Custom single-select dropdown for the Call Activity Log outcome filter —
   matches the "All groups" dropdown on the Product > Modifier Performance
   report (ModifierSingleSelect in ProductMenuPerformanceScreen.tsx) instead
   of a native <select>, which renders with unstyleable OS chrome. */
function OutcomeSingleSelect({ value, allValue, allLabel, options, onChange }: {
    value: string; allValue: string; allLabel: string; options: { id: string; label: string }[]; onChange: (v: string) => void;
}) {
    const [open, setOpen] = useState(false);
    // Mobile's BottomNav is position:fixed with zIndex 1000 (see BottomNav.tsx)
    // and can visually cover / block scrolling on a downward panel opened near
    // the bottom of the viewport. Flip the panel upward and cap its height to
    // whatever space is actually free, so every option stays reachable.
    const [panel, setPanel] = useState<{ direction: "up" | "down"; maxHeight: number }>({ direction: "down", maxHeight: 260 });
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const isDefault = value === allValue;
    const displayLabel = isDefault ? allLabel : (options.find((opt) => opt.id === value)?.label ?? allLabel);

    const BOTTOM_NAV_CLEARANCE = 88; // NAV_HEIGHT (72) + safe-area/padding buffer
    const PREFERRED_HEIGHT = 260;
    const EDGE_MARGIN = 12;

    const openMenu = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (rect) {
            const spaceBelow = window.innerHeight - BOTTOM_NAV_CLEARANCE - rect.bottom - EDGE_MARGIN;
            const spaceAbove = rect.top - EDGE_MARGIN;
            const direction = spaceBelow >= 120 || spaceBelow >= spaceAbove ? "down" : "up";
            const available = direction === "down" ? spaceBelow : spaceAbove;
            setPanel({ direction, maxHeight: Math.max(120, Math.min(PREFERRED_HEIGHT, available)) });
        }
        setOpen((v) => !v);
    };

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    return (
        <div ref={containerRef} style={{ position: "relative", minWidth: 160 }}>
            <div
                ref={triggerRef}
                onClick={openMenu}
                style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
                    height: 34, padding: "0 10px", boxSizing: "border-box",
                    borderRadius: 8, border: `1px solid ${!isDefault ? T.accent : T.cardBorder}`,
                    background: !isDefault ? T.accentSoft : T.surface, cursor: "pointer",
                    fontSize: 12, fontWeight: !isDefault ? 600 : 500,
                    color: !isDefault ? T.accent : T.t1, userSelect: "none", fontFamily: T.font,
                    maxWidth: 220, overflow: "hidden",
                }}
            >
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayLabel}</span>
                <span className="material-symbols-rounded" style={{ fontSize: 16, color: !isDefault ? T.accent : T.t3, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>expand_more</span>
            </div>
            {open && (
                <div
                    style={{
                        position: "absolute",
                        ...(panel.direction === "down" ? { top: "calc(100% + 4px)" } : { bottom: "calc(100% + 4px)" }),
                        right: 0, zIndex: 1001, minWidth: "100%", maxWidth: 320, maxHeight: panel.maxHeight, overflowY: "auto",
                        WebkitOverflowScrolling: "touch", background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                    }}
                >
                    {[{ id: allValue, label: allLabel }, ...options].map((opt) => {
                        const isSelected = opt.id === value;
                        return (
                            <div
                                key={opt.id}
                                onClick={() => { onChange(opt.id); setOpen(false); }}
                                style={{
                                    padding: "8px 12px", fontSize: 12, fontFamily: T.font, cursor: "pointer", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8,
                                    background: isSelected ? T.accentSoft : "transparent", color: isSelected ? T.accent : T.t1, fontWeight: isSelected ? 600 : 400,
                                }}
                                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = T.hover; }}
                                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                            >
                                <span style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4 }}>{opt.label}</span>
                                {isSelected && <span style={{ fontSize: 12, color: T.accent, flexShrink: 0, marginTop: 1 }}>✓</span>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* Call Activity Log — backed by the real, server-paginated + outcome-filtered
   endpoint. The backend has no sort param, so columns are shown in the order
   the API returns them (most recent first) rather than client-sortable. */
function CallActivityLog({ locationId, from, to, period, isDesktop, onOpenCall }: {
    locationId: string; from: string; to: string; period: string; isDesktop: boolean; onOpenCall: (ref: CallRef) => void;
}) {
    const [page, setPage] = useState(1);
    const dispatch = useAppDispatch();
    // Persisted via redux-persist (see voiceAgentIndex.ts's nested persistReducer,
    // whitelist: ['callActivityLogOutcomeFilter']) so a refresh keeps the selected
    // outcome filter instead of resetting to "All outcomes" — same unified
    // component drives both mobile and desktop. Wiped on logout by the existing
    // RESET_REPORTS_DATA handling in rootReducer.ts (voiceAgent: undefined), so a
    // different account never inherits the previous user's selected filter.
    const outcomeFilter = useAppSelector((s) => s.voiceAgent.callActivityLogOutcomeFilter);
    const setOutcomeFilter = (v: string) => dispatch(setCallActivityLogOutcomeFilter(v));
    // 3-click cycle per column header, per VA-5.1: desc -> asc -> reset
    // (undefined = server default, Time descending). `time`, `duration`, and
    // `revenue` are all server-sortable; clicking a header swaps the active
    // sort column and resets to desc.
    type SortColumn = 'time' | 'duration' | 'revenue';
    const [columnSort, setColumnSort] = useState<{ column: SortColumn; order: 'asc' | 'desc' } | undefined>(undefined);
    const pageSize = isDesktop ? 25 : 10;
    const outcomeOptions = useCallActivityLogOutcomes(locationId);
    const sort = columnSort ? { sortBy: columnSort.column, sortOrder: columnSort.order } : {};
    const { rows, pagination, loading } = useCallActivityLog({ locationId, from, to, period }, outcomeFilter, sort, page, pageSize);

    useEffect(() => { setPage(1); }, [from, to, outcomeFilter, columnSort, locationId]);

    const cycleSort = (column: SortColumn) => setColumnSort((prev) => {
        if (!prev || prev.column !== column) return { column, order: "desc" };
        if (prev.order === "desc") return { column, order: "asc" };
        return undefined;
    });

    const totalPages = pagination?.totalPages || 1;
    const total = pagination?.total ?? rows.length;

    const filterSelect = (
        <OutcomeSingleSelect
            value={outcomeFilter}
            allValue="all"
            allLabel="All outcomes"
            options={outcomeOptions.map((o) => ({ id: o, label: titleCaseOutcome(o) }))}
            onChange={setOutcomeFilter}
        />
    );

    // VA-9.1: export ALL rows for the active date range + outcome filter + sort
    // order, not just the current page — same "refetch with size = totalElements"
    // pattern as every other server-paginated report table. Omits from/to for
    // "Today" (see useCallActivityLog's own sendDates), so export can't reintroduce
    // the "explicit today date" bug just fixed.
    const buildExport = async () => {
        const sendDates = period !== "Today";
        const size = total || rows.length || 1;
        const res = await getVoiceAgentCallActivityLog(
            { locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined },
            { outcome: outcomeFilter === "all" ? undefined : outcomeFilter, sortBy: sort.sortBy, sortOrder: sort.sortOrder, page: 1, limit: size },
        );
        return {
            headers: ["Time", "Customer Name", "Customer Number", "Order ID", "Duration", "Outcome", `Revenue (${getCurrencySymbol()})`],
            rows: (res.data ?? []).map((c) => [
                c.time,
                c.customerName || "—",
                c.customerNumber || "—",
                c.orderNo || "—",
                c.duration,
                c.outcome ? titleCaseOutcome(c.outcome) : "—",
                c.revenue != null ? rawNumber(c.revenue) : "—",
            ]),
        };
    };

    const header = (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: T.t1, fontFamily: T.font }}>Call Activity Log</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11.5, color: T.t3, fontFamily: T.font }}>Showing {rows.length} of {total} calls</span>
                {filterSelect}
                {rows.length > 0 && (
                    <ReportSectionExportButton
                        title="Call Activity Log"
                        filename={`voice-agent-call-activity-log-${from}`}
                        buildExport={buildExport}
                        outletScoped={false}
                        iconOnly
                    />
                )}
            </div>
        </div>
    );

    if (loading && !rows.length) return <CallActivityLogShimmer />;
    if (!rows.length && outcomeFilter === "all") return <SectionCard title="Call Activity Log"><EmptyMsg text="No calls recorded for the selected period." /></SectionCard>;

    if (!isDesktop) {
        return (
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: T.cardShadow }}>
                {header}
                {!rows.length ? <EmptyMsg text="No calls match the selected filter." minHeight={100} /> : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {rows.map((c) => (
                            <div key={c.callSid} onClick={() => onOpenCall(c.callSid)} style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, cursor: "pointer", background: T.card }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: T.t1, fontFamily: T.font }}>{c.customerName || "Unknown caller"}</span>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: c.revenue != null ? T.t1 : T.t3, fontFamily: T.font }}>{c.revenue != null ? fmtMoney2(c.revenue) : "—"}</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                                    <span style={{ fontSize: 11.5, color: T.t3, fontFamily: T.font }}>{c.time} · {c.duration}</span>
                                    <span style={{ fontSize: 11.5, color: T.t3, fontFamily: T.font }}>{c.orderNo || "—"}</span>
                                </div>
                                <div style={{ marginTop: 8 }}><OutcomePillReal outcome={c.outcome} /></div>
                            </div>
                        ))}
                    </div>
                )}
                {totalPages > 1 && <Pagination page={page - 1} totalPages={totalPages} setPage={(n) => setPage(n + 1)} />}
            </div>
        );
    }

    const th: React.CSSProperties = { textAlign: "left", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, color: T.t3, padding: "0 0 10px", fontFamily: T.font };
    const td: React.CSSProperties = { fontSize: 13, color: T.t1, padding: "11px 0", fontFamily: T.font, borderTop: `1px solid ${T.divider}` };
    return (
        <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow }}>
            {header}
            {!rows.length ? <EmptyMsg text="No calls match the selected filter." minHeight={120} /> : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={{ ...th, cursor: "pointer", userSelect: "none" }} onClick={() => cycleSort("time")}>
                                Time{columnSort?.column === "time" ? (columnSort.order === "desc" ? " ↓" : " ↑") : ""}
                            </th>
                            <th style={th}>Customer Name</th>
                            <th style={th}>Customer Number</th>
                            <th style={th}>Order ID</th>
                            <th style={{ ...th, cursor: "pointer", userSelect: "none" }} onClick={() => cycleSort("duration")}>
                                Duration{columnSort?.column === "duration" ? (columnSort.order === "desc" ? " ↓" : " ↑") : ""}
                            </th>
                            <th style={th}>Outcome</th>
                            <th style={{ ...th, textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => cycleSort("revenue")}>
                                Revenue{columnSort?.column === "revenue" ? (columnSort.order === "desc" ? " ↓" : " ↑") : ""}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c) => (
                            <tr key={c.callSid} onClick={() => onOpenCall(c.callSid)} style={{ cursor: "pointer" }} className="va-row">
                                <td style={td}>{c.time}</td>
                                <td style={td}>{c.customerName || "Unknown caller"}</td>
                                <td style={{ ...td, color: c.customerNumber ? T.t1 : T.t3 }}>{c.customerNumber || "—"}</td>
                                <td style={{ ...td, color: c.orderNo ? T.t1 : T.t3 }}>{c.orderNo || "—"}</td>
                                <td style={td}>{c.duration}</td>
                                <td style={td}><OutcomePillReal outcome={c.outcome} /></td>
                                <td style={{ ...td, textAlign: "right", fontWeight: 600, color: c.revenue != null ? T.t1 : T.t3 }}>{c.revenue != null ? fmtMoney2(c.revenue) : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {totalPages > 1 && <Pagination page={page - 1} totalPages={totalPages} setPage={(n) => setPage(n + 1)} />}
        </div>
    );
}

function Pagination({ page, totalPages, setPage }: { page: number; totalPages: number; setPage: (n: number) => void }) {
    const btn = (disabled: boolean): React.CSSProperties => ({
        padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface,
        color: disabled ? T.t3 : T.t1, fontSize: 12.5, fontFamily: T.font, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
    });
    return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 16 }}>
            <button style={btn(page === 0)} disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
            <span style={{ fontSize: 12.5, color: T.t2, fontFamily: T.font }}>Page {page + 1} of {totalPages}</span>
            <button style={btn(page >= totalPages - 1)} disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next</button>
        </div>
    );
}

/* Normalized shape both the mock (VACall) and real (CallTranscriptResponse)
   sources render through, so the slide-over JSX only needs one code path. */
interface TranscriptView {
    id: string;
    dateText: string;
    durationText: string;
    outcomeNode: React.ReactNode;
    transcript: { speaker: "agent" | "caller"; text: string; t?: string }[];
    quality: { overall: number; accuracy: number; tone: number; efficiency: number } | null;
    // `items`/`total` are the legacy flat shape — still used by the untouched
    // mock path (viewFromMockCall). `richItems` + the fee/tax/tip fields are
    // populated only by the real API path (viewFromApiCall), which has a full
    // modifier + fee breakdown to show (see CallTranscriptOrder in
    // services/voiceAgentApi.ts) — the slide-over renders whichever is present.
    order?: {
        label: string;
        items?: { text: string; amount: number }[];
        total?: number;
        richItems?: { name: string; modifiersLabel: string; qty: number; unitPrice: number; lineTotal: number }[];
        subtotal?: number;
        discount?: number;
        deliveryFeeDiscount?: number;
        tax?: number;
        tip?: number;
        gratuity?: number;
        deliveryCharges?: number;
        convenienceFee?: number;
        cardProcessingFee?: number;
        grandTotal?: number;
    };
    eightySixed?: EightySixEncounter[];
}

const ORDER_ITEM_GRID = "1.6fr 1.4fr 0.5fr 0.7fr 0.8fr";

function modifiersLabel(mods: CallTranscriptOrderItem['modifiers'] | undefined): string {
    if (!mods || !mods.length) return "—";
    // Size options like `16"` carry a literal inch mark from the backend —
    // drop it here so the pill text doesn't end in a stray quote character.
    return mods.map((m) => m.modifierOptionName.replace(/"/g, "")).join(", ");
}

function viewFromMockCall(call: VACall): TranscriptView {
    const dt = new Date(call.startTime);
    return {
        id: call.callId,
        dateText: dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " · " + dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        durationText: fmtDuration(call.durationSeconds),
        outcomeNode: <OutcomePill outcome={call.outcome} />,
        transcript: call.transcript,
        quality: call.qualityOverall != null ? { overall: call.qualityOverall, accuracy: call.qualityAccuracy || 0, tone: call.qualityTone || 0, efficiency: call.qualityEfficiency || 0 } : null,
        order: call.orderItems && call.orderItems.length > 0 ? {
            label: call.orderId || "",
            items: call.orderItems.map((it) => ({ text: `${it.qty}× ${it.name}${it.modifiers?.length ? ` · ${it.modifiers.join(", ")}` : ""}`, amount: it.qty * it.price })),
            total: call.orderTotal || 0,
        } : undefined,
        eightySixed: call.eightySixed,
    };
}

function viewFromApiCall(resp: CallTranscriptResponse): TranscriptView {
    const quality = mockQualityForKey(resp.callSid);
    return {
        id: resp.callSid,
        dateText: resp.startedAt,
        durationText: resp.duration,
        outcomeNode: <OutcomePillReal outcome={resp.outcome} />,
        transcript: resp.transcript.map((l) => ({ speaker: l.role === "assistant" ? "agent" : "caller", text: l.content })),
        quality,
        order: resp.order ? {
            label: resp.order.orderNo,
            richItems: resp.order.items.map((it) => ({
                name: it.itemName,
                modifiersLabel: modifiersLabel(it.modifiers),
                qty: it.quantity,
                // `price` from the API is the item's line amount (matches the
                // legacy flat rendering's `amount: it.price`), so back out a
                // per-unit figure for the "Unit" column rather than assuming
                // it's already a unit price.
                unitPrice: it.quantity > 0 ? it.price / it.quantity : it.price,
                lineTotal: it.price,
            })),
            subtotal: resp.order.total,
            discount: resp.order.discount,
            deliveryFeeDiscount: resp.order.deliveryFeeDiscount,
            tax: resp.order.tax,
            tip: resp.order.tip,
            gratuity: resp.order.gratuity,
            deliveryCharges: resp.order.deliveryCharges,
            convenienceFee: resp.order.convenienceFee,
            cardProcessingFee: resp.order.cardProcessingFee,
            grandTotal: resp.order.grandTotal,
        } : undefined,
    };
}

/* Transcript slide-over — VA-6.1. `call` is either a full mock VACall
   (untouched drills that still carry sample transcripts inline) or a real
   callSid string (Call Activity Log / Recent Voice Orders), fetched here. */
function TranscriptSlideOver({ call, onClose, isDesktop }: { call: CallRef | null; onClose: () => void; isDesktop: boolean }) {
    const isRealRef = typeof call === "string";
    const real = useCallTranscript(isRealRef ? (call as string) : null);

    const panel: React.CSSProperties = isDesktop
        ? { position: "fixed", top: 0, right: 0, width: 480, height: "100vh", background: T.card, boxShadow: "-4px 0 24px rgba(0,0,0,0.12)", zIndex: 1000, overflowY: "auto" }
        : { position: "fixed", inset: 0, background: T.card, zIndex: 1001, overflowY: "auto", paddingTop: "env(safe-area-inset-top)", paddingBottom: "max(20px, env(safe-area-inset-bottom))" };
    const closeBtn = (
        <div onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, background: T.hover, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t2 }}>close</span>
        </div>
    );

    if (!call) return null;

    if (isRealRef) {
        if (real.loading || (!real.data && !real.error)) {
            return (
                <>
                    {isDesktop && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 999 }} />}
                    <div style={panel}>
                        <div style={{ padding: 20, borderBottom: `1px solid ${T.divider}`, display: "flex", justifyContent: "space-between" }}>
                            <Bone w={160} h={16} r={4} />
                            {closeBtn}
                        </div>
                        <div style={{ padding: 20 }}><Bone h={220} r={8} /></div>
                    </div>
                </>
            );
        }
        if (real.error || !real.data) {
            return (
                <>
                    {isDesktop && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 999 }} />}
                    <div style={panel}>
                        <div style={{ padding: 20, borderBottom: `1px solid ${T.divider}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, fontFamily: T.font }}>Call Transcript</div>
                            {closeBtn}
                        </div>
                        <div style={{ padding: 20 }}><EmptyMsg text={real.error || "Call not found."} /></div>
                    </div>
                </>
            );
        }
    }

    const view = isRealRef ? viewFromApiCall(real.data as CallTranscriptResponse) : viewFromMockCall(call as VACall);

    return (
        <>
            {isDesktop && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 999 }} />}
            <div style={panel}>
                {/* Header */}
                <div style={{ padding: 20, borderBottom: `1px solid ${T.divider}`, position: "sticky", top: 0, background: T.card, zIndex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, fontFamily: T.font }}>{view.order ? `Order #${view.order.label}` : "Call Details"}</div>
                            <div style={{ fontSize: 12.5, color: T.t2, marginTop: 3, fontFamily: T.font }}>{view.dateText} · {view.durationText}</div>
                        </div>
                        {closeBtn}
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
                        {view.outcomeNode}
                    </div>
                </div>

                <div style={{ padding: 20 }}>
                    {/* Order details */}
                    {view.order && (
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: T.t3, marginBottom: 10, fontFamily: T.font }}>Order Details · {view.order.label}</div>
                            {view.order.richItems ? (
                                <>
                                    <div style={{ borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: T.bg, padding: "12px 14px", marginBottom: 12 }}>
                                        <div style={{ display: "grid", gridTemplateColumns: ORDER_ITEM_GRID, gap: 6, padding: "0 0 6px", borderBottom: `1px solid ${T.divider}`, marginBottom: 4, fontFamily: T.font }}>
                                            {["Item", "Modifiers", "Qty", "Unit", "Total"].map((h) => (
                                                <span key={h} style={{ fontSize: 9.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, color: T.t3 }}>{h}</span>
                                            ))}
                                        </div>
                                        {view.order.richItems.map((it, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    display: "grid", gridTemplateColumns: ORDER_ITEM_GRID, gap: 6, padding: "7px 0", fontFamily: T.font,
                                                    borderBottom: i < view.order!.richItems!.length - 1 ? `1px solid ${T.divider}` : "none",
                                                }}
                                            >
                                                <span style={{ fontSize: 12, fontWeight: 500, color: T.t1 }}>{it.name}</span>
                                                <span style={{ fontSize: 11, color: T.t3 }}>{it.modifiersLabel}</span>
                                                <span style={{ fontSize: 12, color: T.t2 }}>{it.qty}</span>
                                                <span style={{ fontSize: 12, color: T.t2 }}>{fmtMoney2(it.unitPrice)}</span>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, textAlign: "right" }}>{fmtMoney2(it.lineTotal)}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: T.bg, padding: "12px 14px", marginBottom: 12 }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: T.t3, marginBottom: 8, fontFamily: T.font }}>Summary</div>
                                        {[
                                            { label: "Subtotal", val: fmtMoney2(view.order.subtotal ?? 0), color: T.t1 },
                                            ...(view.order.discount ? [{ label: "Discount", val: `–${fmtMoney2(view.order.discount)}`, color: T.orange }] : []),
                                            ...(view.order.deliveryFeeDiscount ? [{ label: "Delivery Fee Discount", val: `–${fmtMoney2(view.order.deliveryFeeDiscount)}`, color: T.orange }] : []),
                                            { label: "Tax", val: fmtMoney2(view.order.tax ?? 0), color: T.t1 },
                                            { label: "Tip", val: fmtMoney2(view.order.tip ?? 0), color: T.green },
                                            ...(view.order.gratuity ? [{ label: "Gratuity", val: fmtMoney2(view.order.gratuity), color: T.green }] : []),
                                            ...(view.order.deliveryCharges ? [{ label: "Delivery Charges", val: fmtMoney2(view.order.deliveryCharges), color: T.t1 }] : []),
                                            ...(view.order.convenienceFee ? [{ label: "Convenience Fee", val: fmtMoney2(view.order.convenienceFee), color: T.t1 }] : []),
                                            ...(view.order.cardProcessingFee ? [{ label: "Card Processing Fee", val: fmtMoney2(view.order.cardProcessingFee), color: T.t1 }] : []),
                                        ].map((row) => (
                                            <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontFamily: T.font }}>
                                                <span style={{ fontSize: 12, color: T.t2 }}>{row.label}</span>
                                                <span style={{ fontSize: 12, fontWeight: 500, color: row.color }}>{row.val}</span>
                                            </div>
                                        ))}
                                        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, marginTop: 4, fontFamily: T.font }}>
                                            <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>Total</span>
                                            <span style={{ fontSize: 14, fontWeight: 700, color: T.accent }}>{fmtMoney2(view.order.grandTotal ?? 0)}</span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {view.order.items?.map((it, i) => (
                                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.divider}`, fontFamily: T.font }}>
                                            <span style={{ fontSize: 13, color: T.t1 }}>{it.text}</span>
                                            <span style={{ fontSize: 13, color: T.t2 }}>{fmtMoney2(it.amount)}</span>
                                        </div>
                                    ))}
                                    <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10, fontFamily: T.font }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>Total</span>
                                        <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>{fmtMoney2(view.order.total ?? 0)}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Transcript */}
                    <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: T.t3, marginBottom: 12, fontFamily: T.font }}>Transcript</div>
                    {view.transcript.length <= 1 ? (
                        <div style={{ fontSize: 13, color: T.t3, fontFamily: T.font, marginBottom: 20 }}>Brief call — no substantive transcript recorded.</div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                            {view.transcript.map((line, i) => (
                                <div key={i} style={{ display: "flex", justifyContent: line.speaker === "caller" ? "flex-end" : "flex-start" }}>
                                    <div style={{ maxWidth: "82%", padding: "8px 12px", borderRadius: 12, background: line.speaker === "caller" ? T.accentSoft : T.hover, fontFamily: T.font }}>
                                        <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: T.t3, marginBottom: 2 }}>{line.speaker === "caller" ? "Caller" : "Agent"}{line.t ? ` · ${line.t}` : ""}</div>
                                        <div style={{ fontSize: 13, color: T.t1, lineHeight: 1.4 }}>{line.text}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 86'd items — mock-only, the real transcript endpoint doesn't return these */}
                    {view.eightySixed && view.eightySixed.length > 0 && (
                        <div>
                            <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: T.t3, marginBottom: 10, fontFamily: T.font }}>86'd Items Encountered</div>
                            {view.eightySixed.map((e, i) => (
                                <div key={i} style={{ padding: "8px 12px", borderRadius: 10, background: T.orangeSoft, marginBottom: 8, fontFamily: T.font }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: T.t1 }}>{e.itemName} — unavailable</div>
                                    <div style={{ fontSize: 12, color: T.t2, marginTop: 2 }}>
                                        Offered: {e.alternativeOffered || "—"} · Customer {e.customerAction.replace(/_/g, " ")}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

/* Time Saved ROI card — VA-4, real API data. */
function TimeSavedCard({ data, loading, style }: { data: TimeSavedResponse | null; loading: boolean; style?: React.CSSProperties }) {
    const sym = useCurrencySymbol();
    return (
        <SectionCard title="Estimated Time Saved"
            info="Calculated as: calls handled without transfer × average call duration. Labour cost assumes an hourly rate provided by the backend. Actual savings may vary."
            style={{ background: `linear-gradient(180deg, ${T.accentSoft}, ${T.card} 60%)`, ...style }}>
            {loading && !data ? <Bone h={70} r={8} /> : (
                <>
                    <div style={{ fontSize: 28, fontWeight: 800, color: T.t1, fontFamily: T.font, letterSpacing: -0.5 }}>{fmtHrsMin(60 * (data?.hours || 0) + (data?.minutes || 0))}</div>
                    <div style={{ fontSize: 13.5, color: T.t2, marginTop: 6, fontFamily: T.font }}>≈ {sym}{Math.round(data?.laborCostEstimate || 0).toLocaleString("en-US")} at {sym}{data?.hourlyRate ?? 15}/hr</div>
                    <div style={{ fontSize: 11.5, color: T.t3, marginTop: 10, fontFamily: T.font }}>
                        {(data?.callsHandledWithoutTransfer ?? 0).toLocaleString("en-US")} calls handled without transfer · avg {data?.avgCallDurationMinutes ?? 0} min/call
                    </div>
                </>
            )}
        </SectionCard>
    );
}

/* Desktop stamp-KPI form of Estimated Time Saved — sits in the KPI grid slot
   Conversion Rate used to occupy. Same data as TimeSavedCard (mobile keeps that). */
function TimeSavedStampKPI({ data, loading }: { data: TimeSavedResponse | null; loading: boolean }) {
    const sym = useCurrencySymbol();
    const calls = (data?.callsHandledWithoutTransfer ?? 0).toLocaleString("en-US");
    return (
        <div style={{ position: "relative", background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: "14px 16px", boxShadow: T.cardShadow, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: T.t3, fontFamily: T.font }}>
                ESTIMATED TIME SAVED
                <KpiTooltip text={`Calculated as: calls handled without transfer × average call duration. Labour cost assumes an hourly rate provided by the backend. Actual savings may vary. ${calls} calls handled without transfer · avg ${data?.avgCallDurationMinutes ?? 0} min/call.`} />
            </div>
            {loading && !data ? (
                <Bone w={70} h={28} r={4} mb={4} style={{ marginTop: 5 }} />
            ) : (
                <>
                    <div style={{ fontSize: 24, fontWeight: 700, color: T.t1, marginTop: 5, fontFamily: T.font, lineHeight: 1.1 }}>{fmtHrsMin(60 * (data?.hours || 0) + (data?.minutes || 0))}</div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.t3, marginTop: 4, fontFamily: T.font }}>≈ {sym}{Math.round(data?.laborCostEstimate || 0).toLocaleString("en-US")} at {sym}{data?.hourlyRate ?? 15}/hr</div>
                </>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI MODEL
   ═══════════════════════════════════════════════════════════════════════════ */

type DrillKey = "calls" | "orders" | "conversion" | "revenue" | "transfers" | "eightysix";

/* Convert a backend-precomputed relative-% / percentage-point delta into the
   local Delta shape. null means "N/A" per the API contract (no arrow, gray). */
function apiDeltaPct(v: number | null | undefined, invert = false): Delta {
    if (v == null) return { text: "N/A", tone: "neutral", positive: false };
    const up = v >= 0;
    const positive = invert ? !up : up;
    return { text: `${up ? "↑" : "↓"} ${Math.abs(v).toFixed(1)}%`, tone: up ? "up" : "down", positive };
}
function apiDeltaPP(v: number | null | undefined, invert = false): Delta {
    if (v == null) return { text: "N/A", tone: "neutral", positive: false };
    const up = v >= 0;
    const positive = invert ? !up : up;
    return { text: `${up ? "↑" : "↓"} ${Math.abs(v).toFixed(1)}pp`, tone: up ? "up" : "down", positive };
}

/* 5 of the 6 stamp KPIs come from the Summary API; 86'd Impact has no backend
   support yet so its card is commented out below rather than shown on the
   mock pool (see project notes).
   `loading` reflects the real in-flight fetch (always false by the time this
   runs today, since the parent gates the whole page behind a full shimmer
   until the summary call settles) — kept explicit rather than inferred from
   `!summary`, so a genuine fetch failure shows "—" instead of an infinite
   shimmer. */
function buildKpis(mock: VoiceAgentData, summary: VoiceAgentSummary | null, isDesktop: boolean, loading: boolean) {
    // const c = mock.current, p = mock.prior; // only used by the commented-out 86'd Impact card below
    return [
        { key: "calls" as DrillKey, label: isDesktop ? "TOTAL CALLS" : "CALLS", value: summary ? fmtCompact(summary.totalCalls) : "—", delta: summary ? apiDeltaPct(summary.totalCallsDeltaPercent) : undefined, loading },
        { key: "orders" as DrillKey, label: isDesktop ? "ORDERS PLACED" : "ORDERS", value: summary ? fmtCompact(summary.ordersPlaced) : "—", delta: summary ? apiDeltaPct(summary.ordersPlacedDeltaPercent) : undefined, loading },
        { key: "conversion" as DrillKey, label: "CONVERSION RATE", value: summary ? fmtPct(summary.conversionRate) : "—", delta: summary ? apiDeltaPP(summary.conversionRateDeltaPp) : undefined, loading },
        { key: "revenue" as DrillKey, label: isDesktop ? "VOICE REVENUE" : "REVENUE", value: summary ? fmtMoneyCompact(summary.totalRevenue) : "—", delta: summary ? apiDeltaPct(summary.totalRevenueDeltaPercent) : undefined, loading },
        { key: "transfers" as DrillKey, label: isDesktop ? "TRANSFER RATE" : "TRANSFERS", value: summary ? fmtPct(summary.transferRate) : "—", delta: summary ? apiDeltaPP(summary.transferRateDeltaPp, true) : undefined, loading },
        // 86'd Impact — no backend endpoint yet, hidden until one exists.
        // { key: "eightysix" as DrillKey, label: "86'D IMPACT", value: fmtCompact(c.eightySixEncounters), delta: deltaPct(c.eightySixEncounters, p.eightySixEncounters, true), loading: false },
    ];
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */

function MainDashboard({ data, summary, topItems, topItemsLoading, timeSaved, timeSavedLoading, callVolumeByHour, callVolumeByHourLoading, isDesktop, onDrill, onOpenCall, locationId, from, to, period }: {
    data: VoiceAgentData; summary: VoiceAgentSummary | null; topItems: TopItemRow[]; topItemsLoading: boolean;
    timeSaved: TimeSavedResponse | null; timeSavedLoading: boolean;
    callVolumeByHour: CallVolumeByHourItem[]; callVolumeByHourLoading: boolean;
    isDesktop: boolean; onDrill: (k: DrillKey) => void; onOpenCall: (ref: CallRef) => void;
    locationId: string; from: string; to: string; period: string;
}) {
    const kpis = buildKpis(data, summary, isDesktop, topItemsLoading);

    // Conversion Rate and Transfer Rate are hidden on both desktop and mobile;
    // on desktop, Estimated Time Saved takes the Conversion Rate slot (3rd).
    const visibleKpis = kpis.filter((k) => k.key !== "conversion" && k.key !== "transfers");
    const kpiSection = isDesktop ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            {visibleKpis.slice(0, 2).map((k) => (
                <KPICard key={k.key} id={`va-kpi-${k.key}`} label={k.label} value={k.value} delta={k.delta} drillable onTap={() => onDrill(k.key)} loading={k.loading} />
            ))}
            <TimeSavedStampKPI data={timeSaved} loading={timeSavedLoading} />
            {visibleKpis.slice(2).map((k) => (
                <KPICard key={k.key} id={`va-kpi-${k.key}`} label={k.label} value={k.value} delta={k.delta} drillable onTap={() => onDrill(k.key)} loading={k.loading} />
            ))}
        </div>
    ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {visibleKpis.map((k) => <KPICard key={k.key} id={`va-kpi-${k.key}`} label={k.label} value={k.value} delta={k.delta} drillable onTap={() => onDrill(k.key)} compact loading={k.loading} />)}
        </div>
    );

    // Desktop: Estimated Time Saved lives in the KPI grid above, so only Call Volume + Top Items remain here.
    const chartsAndRoi = isDesktop ? (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 58%", display: "flex", flexDirection: "column", gap: 16 }}>
                <SectionCard title="Call Volume by Hour"><HourVolumeChart items={callVolumeByHour} loading={callVolumeByHourLoading} /></SectionCard>
            </div>
            <div style={{ flex: "1 1 42%" }}><SectionCard title="Top Items Ordered via Voice"><TopItemsChart items={topItems} loading={topItemsLoading} /></SectionCard></div>
        </div>
    ) : (
        <>
            <SectionCard title="Call Volume by Hour"><HourVolumeChart items={callVolumeByHour} loading={callVolumeByHourLoading} /></SectionCard>
            <SectionCard title="Top Items Ordered via Voice"><TopItemsChart items={topItems} loading={topItemsLoading} /></SectionCard>
            <TimeSavedCard data={timeSaved} loading={timeSavedLoading} />
        </>
    );

    return (
        <div className={isDesktop ? "rpt-page" : undefined} style={isDesktop ? undefined : { padding: 14, display: "flex", flexDirection: "column", gap: 14 }}>
            {kpiSection}
            {chartsAndRoi}
            <CallActivityLog locationId={locationId} from={from} to={to} period={period} isDesktop={isDesktop} onOpenCall={onOpenCall} />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL-IN PAGES — VA-7.1 … VA-7.7
   ═══════════════════════════════════════════════════════════════════════════ */

const DRILL_TITLES: Record<DrillKey, string> = {
    calls: "Total Calls", orders: "Orders Placed", conversion: "Conversion Rate",
    revenue: "Voice Revenue", transfers: "Transfer Rate", eightysix: "86'd Impact",
};

function MiniRow({ children }: { children: React.ReactNode }) {
    return <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>{children}</div>;
}

/* VA-7.1 Total Calls — mini-KPIs from the already-fetched Summary API
   (Total Calls, Avg Calls/Day, Busiest Day all come precomputed from the
   backend), chart from the real Call Volume by Day API, breakdown from the
   real Outcome Distribution API. */
function DrillCalls({ summary, locationId, from, to, period }: {
    summary: VoiceAgentSummary | null; locationId: string; from: string; to: string; period: string;
}) {
    // Call Volume by Day is hidden whenever the selected range collapses to a
    // single day (Today, Yesterday, or a Custom range with from === to) — a
    // one-bar chart carries no trend signal, per VA-7.1.
    const showByDay = from !== to;
    const { days, granularity: daysGranularity, loading: daysLoading } = useCallVolumeByDay({ locationId, from, to, period }, showByDay);
    const chartRows = days.map((d) => ({ label: bucketLabel(d, daysGranularity), value: d.totalCalls }));

    const { outcomes: outcomesRaw, loading: outcomesLoading } = useOutcomeDistribution({ locationId, from, to, period });
    const outcomes = [...outcomesRaw].sort((a, b) => b.count - a.count);
    const maxOutcome = Math.max(...outcomes.map((o) => o.count), 1);

    const busiestLabel = summary?.busiestDay ? new Date(summary.busiestDay.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : "—";

    return (
        <>
            <MiniRow>
                <MiniKPI label="Total Calls" value={summary ? fmtInt(summary.totalCalls) : "—"} loading={!summary} />
                <MiniKPI label="Avg Calls / Day" value={summary ? fmtInt(summary.avgCallsPerDay) : "—"} loading={!summary} />
                <MiniKPI label="Busiest Day" value={summary?.busiestDay ? `${summary.busiestDay.count}` : "—"} sub={busiestLabel} loading={!summary} />
                <MiniKPI label="Total Unique Callers" value={summary?.totalUniqueCalls != null ? fmtInt(summary.totalUniqueCalls) : "—"} loading={!summary} />
            </MiniRow>
            {showByDay && (
                <SectionCard title={`Call Volume by ${BUCKET_HEADING[daysGranularity]}`}>
                    {daysLoading && !chartRows.length ? <Bone h={230} r={8} /> : chartRows.length > 1 ? <GenericByDayChart rows={chartRows} unitLabel="Calls" isMoney={false} granularity={daysGranularity} /> : <EmptyMsg text="Not enough data to chart yet." minHeight={120} />}
                </SectionCard>
            )}
            <SectionCard title="Outcome Distribution">
                {outcomesLoading && !outcomes.length ? <Bone h={140} r={8} /> : !outcomes.length ? <EmptyMsg text="No calls recorded for the selected period." minHeight={140} /> : (
                    outcomes.map((o) => {
                        const m = outcomeMeta(o.outcome);
                        return <BarRow key={o.outcome} label={m.label} valueText={`${o.count}`} ratio={o.count / maxOutcome} color={m.color} />;
                    })
                )}
            </SectionCard>
        </>
    );
}

/* VA-7.2 Orders Placed */
/* Orders Placed drill — mini-KPIs from Summary (already fetched on the main
   dashboard), chart from Orders By Day, table from the paginated Orders
   endpoint. `status` is always null per the API contract for now, so the
   Status column renders a dash rather than a fabricated pill. */
function DrillOrders({ summary, locationId, from, to, period, isDesktop, onOpenCall }: {
    summary: VoiceAgentSummary | null; locationId: string; from: string; to: string; period: string;
    isDesktop: boolean; onOpenCall: (ref: CallRef) => void;
}) {
    const [page, setPage] = useState(1);
    const pageSize = isDesktop ? 25 : 10;
    // Orders by Day is hidden whenever the selected range collapses to a
    // single day (Today, Yesterday, or a Custom range with from === to) — a
    // one-bar chart carries no trend signal, per VA-7.1.
    const showByDay = from !== to;
    const { days, granularity: daysGranularity, loading: daysLoading } = useOrdersByDay({ locationId, from, to, period }, showByDay);
    const { rows, pagination, loading: ordersLoading } = useRecentVoiceOrders({ locationId, from, to, period }, true, page, pageSize);
    useEffect(() => { setPage(1); }, [from, to, locationId]);

    const chartRows = days.map((d) => ({ label: bucketLabel(d, daysGranularity), value: d.count }));
    const totalPages = pagination?.totalPages || 1;
    const total = pagination?.total ?? rows.length;

    // Same "refetch with size = totalElements" pattern as Call Activity Log's
    // export (VA-9.1) — the visible table shows one page, export needs all rows
    // for the active date range. Omits from/to for "Today" for the same reason.
    const buildOrdersExport = async () => {
        const sendDates = period !== "Today";
        const size = total || rows.length || 1;
        const res = await getVoiceAgentOrders(
            { locationId, from: sendDates ? from : undefined, to: sendDates ? to : undefined },
            1,
            size,
        );
        return {
            headers: ["Order ID", "Time", "Items", `Amount (${getCurrencySymbol()})`, "Status"],
            rows: (res.data ?? []).map((o) => [
                o.orderNo || "—",
                o.time,
                o.itemsPreview?.length ? o.itemsPreview.map((it) => it.name).join(", ") : "—",
                rawNumber(o.amount),
                o.status || "—",
            ]),
        };
    };

    return (
        <>
            <MiniRow>
                <MiniKPI label="Total Orders" value={summary ? fmtInt(summary.ordersPlaced) : "—"} />
                <MiniKPI label="Avg Order Value" value={summary ? fmtMoney2(summary.avgOrderValue) : "—"} />
                <MiniKPI label="Avg Items / Order" value={summary ? summary.avgItemsPerOrder.toFixed(1) : "—"} />
            </MiniRow>
            {showByDay && (
                <SectionCard title={`Orders by ${BUCKET_HEADING[daysGranularity]}`}>
                    {daysLoading && !chartRows.length ? <Bone h={230} r={8} /> : chartRows.length > 1 ? <GenericByDayChart rows={chartRows} unitLabel="Orders" isMoney={false} granularity={daysGranularity} /> : <EmptyMsg text="Not enough data to chart yet." minHeight={120} />}
                </SectionCard>
            )}
            <SectionCard
                title="Recent Voice Orders"
                right={rows.length > 0 && (
                    <ReportSectionExportButton
                        title="Recent Voice Orders"
                        filename={`voice-agent-recent-orders-${from}`}
                        buildExport={buildOrdersExport}
                        outletScoped={false}
                        iconOnly
                    />
                )}
            >
                {ordersLoading && !rows.length ? <Bone h={160} r={8} /> : (
                    <>
                        <SimpleTable
                            cols={["Order ID", "Time", "Items", "Amount", "Status"]}
                            aligns={["left", "left", "left", "right", "left"]}
                            widths={["20%", "20%", "20%", "20%", "20%"]}
                            rows={rows.map((o) => {
                                const itemsText = o.itemsPreview?.length ? o.itemsPreview.map((it) => it.name).join(", ") : "—";
                                return {
                                    onClick: () => onOpenCall(o.callSid),
                                    cells: [
                                        o.orderNo || "—",
                                        o.time,
                                        <span title={itemsText} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{itemsText}</span>,
                                        fmtMoney2(o.amount),
                                        o.status || "—",
                                    ],
                                };
                            })}
                            isDesktop={isDesktop}
                        />
                        {totalPages > 1 && <Pagination page={page - 1} totalPages={totalPages} setPage={(n) => setPage(n + 1)} />}
                    </>
                )}
            </SectionCard>
        </>
    );
}

/* VA-7.3 Conversion Funnel — real API, drop counts/percents come precomputed
   from the backend (each relative to its own immediately-preceding stage). */
function DrillConversion({ locationId, from, to, period }: { locationId: string; from: string; to: string; period: string }) {
    const { funnel: f, loading } = useConversionFunnel({ locationId, from, to, period }, true);

    if (loading && !f) return <SectionCard><Bone h={260} r={8} /></SectionCard>;
    if (!f) return <SectionCard><EmptyMsg text="No conversion funnel data for the selected period." minHeight={220} /></SectionCard>;

    const stages = [
        { label: "Total Calls", count: f.totalCalls, dropCount: 0, dropPercent: 0 },
        { label: "Ordering Intent", count: f.orderingIntent, dropCount: f.orderingIntentDropCount, dropPercent: f.orderingIntentDropPercent },
        { label: "Orders Started", count: f.ordersStarted, dropCount: f.ordersStartedDropCount, dropPercent: f.ordersStartedDropPercent },
        { label: "Orders Confirmed", count: f.ordersConfirmed, dropCount: f.ordersConfirmedDropCount, dropPercent: f.ordersConfirmedDropPercent },
        { label: "Orders Submitted", count: f.ordersSubmitted, dropCount: f.ordersSubmittedDropCount, dropPercent: f.ordersSubmittedDropPercent },
        // Not a sequential funnel step off Orders Submitted (a transfer can
        // happen at any point in the call) — shown as its own bar off Total
        // Calls, so it deliberately carries no "dropped off" line.
        { label: "Order Transfers", count: f.totalTransfers, dropCount: 0, dropPercent: 0 },
    ];
    const base = f.totalCalls || 1;
    return (
        <>
            <MiniRow>
                <MiniKPI label="Total Calls" value={fmtInt(f.totalCalls)} />
                <MiniKPI label="Ordering Intent" value={fmtInt(f.orderingIntent)} />
                <MiniKPI label="Orders Started" value={fmtInt(f.ordersStarted)} />
                <MiniKPI label="Orders Submitted" value={fmtInt(f.ordersSubmitted)} />
            </MiniRow>
            <SectionCard title="Conversion Funnel">
                {stages.map((s, i) => (
                    <div key={s.label} style={{ marginBottom: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontFamily: T.font }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: T.t1 }}>{s.label}</span>
                            <span style={{ fontSize: 12.5, color: T.t2 }}>{fmtInt(s.count)} ({((s.count / base) * 100).toFixed(1)}%)</span>
                        </div>
                        <div style={{ height: 22, borderRadius: 6, background: T.hover, overflow: "hidden" }}>
                            <div style={{ width: `${s.count > 0 ? Math.max(2, (s.count / base) * 100) : 0}%`, height: "100%", background: T.accent, borderRadius: 6 }} />
                        </div>
                        {i > 0 && s.dropCount > 0 && (
                            <div style={{ fontSize: 11.5, color: T.t2, marginTop: 4, fontFamily: T.font }}>−{fmtInt(s.dropCount)} ({s.dropPercent.toFixed(1)}%) dropped off</div>
                        )}
                    </div>
                ))}
                <div style={{ marginTop: 8, padding: "10px 14px", borderRadius: 10, background: T.accentSoft, fontSize: 12.5, color: T.t1, fontFamily: T.font }}>
                    Intent-to-Order rate: <b>{f.intentToOrderRate.toFixed(1)}%</b> ({fmtInt(f.ordersSubmitted)} of {fmtInt(f.orderingIntent)} ordering-intent calls converted)
                </div>
            </SectionCard>
        </>
    );
}

/* VA-7.4 Voice Revenue — mini-KPIs from the real Voice Revenue KPI API,
   Revenue by Day chart from the real Call Volume by Day API (it already
   carries a per-day `revenue` figure — same endpoint DrillCalls uses for
   Call Volume by Day), Top Revenue Items list from the real Top Revenue
   Items API. */
function DrillRevenue({ locationId, from, to, period }: {
    locationId: string; from: string; to: string; period: string;
}) {
    const { revenueKpi, loading } = useRevenueKpi({ locationId, from, to, period });
    const { items: topItems, loading: topItemsLoading } = useTopRevenueItems({ locationId, from, to, period });
    const maxRev = topItems.length ? topItems[0].revenue : 1;

    // Revenue by Day is hidden whenever the selected range collapses to a
    // single day (Today, Yesterday, or a Custom range with from === to) — a
    // one-bar chart carries no trend signal, per VA-7.1.
    const showByDay = from !== to;
    const { days, granularity: daysGranularity, loading: daysLoading } = useCallVolumeByDay({ locationId, from, to, period }, showByDay);
    const revenueByDayRows = days.map((d) => ({ label: bucketLabel(d, daysGranularity), value: d.revenue }));

    return (
        <>
            <MiniRow>
                <MiniKPI label="Total Revenue" value={revenueKpi ? fmtMoney2(revenueKpi.totalRevenue) : "—"} loading={loading} />
                <MiniKPI label="Avg Order Value" value={revenueKpi ? fmtMoney2(revenueKpi.avgOrderValue) : "—"} loading={loading} />
                <MiniKPI label="Top Item by Revenue" value={revenueKpi?.topItemByRevenue ? fmtMoney2(revenueKpi.topItemRevenue) : "—"} sub={revenueKpi?.topItemByRevenue ?? undefined} loading={loading} />
            </MiniRow>
            {showByDay && (
                <SectionCard title={`Revenue by ${BUCKET_HEADING[daysGranularity]}`}>
                    {daysLoading && !revenueByDayRows.length ? <Bone h={230} r={8} /> : revenueByDayRows.length > 1 ? <GenericByDayChart rows={revenueByDayRows} unitLabel="Revenue" isMoney granularity={daysGranularity} /> : <EmptyMsg text="Not enough data to chart yet." minHeight={120} />}
                </SectionCard>
            )}
            <SectionCard title="Top Revenue Items">
                {topItemsLoading && !topItems.length ? <Bone h={220} r={8} /> : topItems.length === 0 ? <EmptyMsg text="No voice orders recorded for the selected period." /> :
                    topItems.map((it, i) => (
                        <BarRow key={it.name} label={`${i + 1}. ${it.name}`} valueText={`${fmtMoney2(it.revenue)} · ${it.orders} orders`}
                            ratio={it.revenue / maxRev} color={i === 0 ? T.accent : `${T.accent}80`}
                            sub={`${it.percentageOfRevenue.toFixed(1)}% of voice revenue`} />
                    ))}
            </SectionCard>
        </>
    );
}

/* VA-7.5 Transfer Rate — Total Transfers, Transfer Rate, and the Transfer
   Reasons breakdown come from the pre-aggregated Transfer Reasons API; the
   Recent Transfers table uses the raw per-call Transfers API (the reasons
   endpoint has no callId/time/excerpt fields to power a table). */
function DrillTransfers({ locationId, from, to, period, isDesktop, onOpenCall }: {
    locationId: string; from: string; to: string; period: string; isDesktop: boolean; onOpenCall: (ref: CallRef) => void;
}) {
    const { transfers: t, loading: transfersLoading } = useTransfers({ locationId, from, to, period }, true);
    const { transferReasons: r, loading: reasonsLoading } = useTransferReasons({ locationId, from, to, period }, true);

    const loading = (transfersLoading && !t) || (reasonsLoading && !r);
    if (loading) return <SectionCard><Bone h={260} r={8} /></SectionCard>;
    if (!t || !r || r.totalTransfers === 0) {
        return <SectionCard><EmptyMsg text="No calls were transferred during the selected period — the agent handled everything." minHeight={220} /></SectionCard>;
    }

    const maxReason = Math.max(...r.reasons.map((x) => x.count), 1);

    return (
        <>
            <MiniRow>
                <MiniKPI label="Total Transfers" value={fmtInt(r.totalTransfers)} />
                <MiniKPI label="Transfer Rate" value={fmtPct(r.transferRate)} />
            </MiniRow>
            <SectionCard title="Transfer Reasons">
                {r.reasons.length === 0 ? <EmptyMsg text="No transfer reasons recorded for the selected period." minHeight={100} /> :
                    r.reasons.map((x) => (
                        <BarRow key={x.reasonLabel} label={x.reasonLabel} valueText={`${x.count}× · ${x.sharePct.toFixed(0)}%`} ratio={x.count / maxReason} color={T.orange} />
                    ))}
            </SectionCard>
            <SectionCard title="Recent Transfers">
                <SimpleTable
                    cols={["Time", "Duration", "Reason", "Caller Excerpt"]}
                    aligns={["left", "left", "left", "left"]}
                    rows={t.transfers.slice(0, 30).map((row) => ({
                        onClick: () => onOpenCall(row.callId),
                        cells: [
                            fmtCallTime(row.startTime, false),
                            fmtDuration(row.durationSeconds),
                            row.transferReason ? titleCaseOutcome(row.transferReason) : "Other",
                            (row.callerExcerpt || "—").slice(0, 50),
                        ],
                    }))}
                    isDesktop={isDesktop}
                />
            </SectionCard>
        </>
    );
}

/* VA-7.6 86'd Impact */
function DrillEightySix({ data }: { data: VoiceAgentData }) {
    if (data.current.eightySixEncounters === 0) {
        return <SectionCard><EmptyMsg text="No 86'd item encounters — all menu items were available during the selected period." minHeight={220} /></SectionCard>;
    }
    const seg = (it: typeof data.eightySixItems[0]) => {
        const total = it.encounters || 1;
        const parts = [
            { v: it.accepted, c: T.green }, { v: it.removed, c: T.orange }, { v: it.abandoned, c: T.red }, { v: it.transferred, c: T.t3 },
        ];
        return (
            <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginTop: 6 }}>
                {parts.map((p, i) => p.v > 0 && <div key={i} style={{ width: `${(p.v / total) * 100}%`, background: p.c }} />)}
            </div>
        );
    };
    return (
        <>
            <MiniRow>
                <MiniKPI label="Total 86'd Encounters" value={fmtInt(data.current.eightySixEncounters)} />
                <MiniKPI label="Est. Revenue Impact" value={`≈${fmtMoney(data.eightySixRevenueImpact)}`} />
                <MiniKPI label="Substitution Success" value={fmtPct(data.substitutionSuccessRate)} />
            </MiniRow>
            <SectionCard title="86'd Items">
                {data.eightySixItems.map((it) => (
                    <div key={it.name} style={{ marginBottom: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontFamily: T.font }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: T.t1 }}>{it.name}</span>
                            <span style={{ fontSize: 12.5, color: T.t2 }}>{it.encounters}× · ≈{fmtMoney2(it.revenueImpact)}</span>
                        </div>
                        {seg(it)}
                    </div>
                ))}
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 11, color: T.t2, fontFamily: T.font }}>
                    <LegendDot color={T.green} text="Accepted Alternative" />
                    <LegendDot color={T.orange} text="Removed Item" />
                    <LegendDot color={T.red} text="Abandoned Order" />
                    <LegendDot color={T.t3} text="Transferred" />
                </div>
            </SectionCard>
        </>
    );
}

function LegendDot({ color, text }: { color: string; text: string }) {
    return <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: color }} />{text}</span>;
}


/* Reusable simple table (drill-in tables) — responsive to card list on mobile */
function SimpleTable({ cols, aligns, rows, isDesktop, widths }: {
    cols: string[]; aligns: ("left" | "right")[]; rows: { cells: React.ReactNode[]; onClick?: () => void }[]; isDesktop: boolean; widths?: string[];
}) {
    if (!rows.length) return <EmptyMsg text="No records." minHeight={80} />;
    if (!isDesktop) {
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {rows.map((r, ri) => (
                    <div key={ri} onClick={r.onClick} style={{ border: `1px solid ${T.divider}`, borderRadius: 10, padding: 10, cursor: r.onClick ? "pointer" : "default" }}>
                        {r.cells.map((cell, ci) => (
                            <div key={ci} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", fontFamily: T.font, gap: 10 }}>
                                <span style={{ fontSize: 11, color: T.t3, flexShrink: 0 }}>{cols[ci]}</span>
                                <span style={{ fontSize: 12.5, color: T.t1, textAlign: "right", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cell}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    }
    const th: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, color: T.t3, padding: "0 8px 10px", fontFamily: T.font };
    const td: React.CSSProperties = { fontSize: 13, color: T.t1, padding: "10px 8px", fontFamily: T.font, borderTop: `1px solid ${T.divider}` };
    return (
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: widths ? "fixed" : "auto" }}>
            {widths && <colgroup>{widths.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>}
            <thead><tr>{cols.map((c, i) => <th key={i} style={{ ...th, textAlign: aligns[i] }}>{c}</th>)}</tr></thead>
            <tbody>
                {rows.map((r, ri) => (
                    <tr key={ri} onClick={r.onClick} style={{ cursor: r.onClick ? "pointer" : "default" }} className="va-row">
                        {r.cells.map((cell, ci) => <td key={ci} style={{ ...td, textAlign: aligns[ci] }}>{cell}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function DrillContent({ drillKey, data, summary, locationId, from, to, period, isDesktop, onOpenCall }: {
    drillKey: DrillKey; data: VoiceAgentData; summary: VoiceAgentSummary | null;
    locationId: string; from: string; to: string; period: string;
    isDesktop: boolean; onOpenCall: (ref: CallRef) => void;
}) {
    const wrap = (children: React.ReactNode) => (
        <div className={isDesktop ? "rpt-page" : undefined} style={isDesktop ? undefined : { padding: 14, display: "flex", flexDirection: "column", gap: 14 }}>
            {children}
        </div>
    );
    switch (drillKey) {
        case "calls": return wrap(<DrillCalls summary={summary} locationId={locationId} from={from} to={to} period={period} />);
        case "orders": return wrap(<DrillOrders summary={summary} locationId={locationId} from={from} to={to} period={period} isDesktop={isDesktop} onOpenCall={onOpenCall} />);
        case "conversion": return wrap(<DrillConversion locationId={locationId} from={from} to={to} period={period} />);
        case "revenue": return wrap(<DrillRevenue locationId={locationId} from={from} to={to} period={period} />);
        case "transfers": return wrap(<DrillTransfers locationId={locationId} from={from} to={to} period={period} isDesktop={isDesktop} onOpenCall={onOpenCall} />);
        case "eightysix": return wrap(<DrillEightySix data={data} />);
    }
}

/* ═══════════════════════════════════════════════════════════════════════════
   SHIMMERS
   ═══════════════════════════════════════════════════════════════════════════ */

function DesktopShimmer() {
    const card = { background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 16, boxShadow: T.cardShadow };
    return (
        <div className="rpt-page">
            {/* Matches MainDashboard's actual desktop kpiSection: a single CSS
                Grid row of 4 (Calls, Orders, Estimated Time Saved, Revenue) —
                Conversion Rate and Transfer Rate are hidden on desktop. Uses
                the same Grid layout as the real cards rather than flex-wrap,
                so this can't wrap into a mismatched second row. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} style={card}>
                        <Bone w={80} h={10} r={4} mb={10} /><Bone h={26} r={6} mb={8} /><Bone w={60} h={12} r={4} />
                    </div>
                ))}
            </div>
            <div style={{ display: "flex", gap: 16 }}>
                <div style={{ flex: "1 1 58%", background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 20 }}><Bone w={160} h={14} r={4} mb={14} /><Bone h={230} r={8} /></div>
                <div style={{ flex: "1 1 42%", background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 20 }}><Bone w={160} h={14} r={4} mb={14} /><Bone h={230} r={8} /></div>
            </div>
            {/* Same fallback CallActivityLog itself renders while loading (see
                its own `if (loading && !rows.length)` branch below) — reusing
                it here means there's no visual jump when this page shimmer
                hands off to the component's own shimmer. */}
            <CallActivityLogShimmer />
        </div>
    );
}

function MobileShimmer() {
    return (
        <div style={{ padding: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {/* Matches MainDashboard's actual mobile visibleKpis: Calls,
                    Orders, Revenue — Conversion Rate and Transfer Rate are hidden. */}
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 10, boxShadow: T.cardShadow }}>
                        <Bone w={50} h={8} r={3} mb={7} /><Bone h={18} r={4} mb={5} /><Bone w={36} h={9} r={3} />
                    </div>
                ))}
            </div>
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 16, marginTop: 14 }}><Bone w={140} h={14} r={4} mb={14} /><Bone h={200} r={8} /></div>
            {/* Same fallback CallActivityLog itself renders while loading —
                see the matching comment in DesktopShimmer above. */}
            <div style={{ marginTop: 14 }}><CallActivityLogShimmer /></div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DESKTOP VIEW
   ═══════════════════════════════════════════════════════════════════════════ */

interface VoiceAgentApiCtx {
    locationId: string; from: string; to: string;
    summary: VoiceAgentSummary | null; topItems: TopItemRow[]; timeSaved: TimeSavedResponse | null; loading: boolean;
    callVolumeByHour: CallVolumeByHourItem[]; callVolumeByHourLoading: boolean;
}

function DesktopView({ data, api, period, setPeriod }: {
    data: VoiceAgentData; api: VoiceAgentApiCtx; period: string; setPeriod: (p: string) => void;
}) {
    const setTopBarProps = useDesktopTopBar();
    const [drillKey, setDrillKey] = useState<DrillKey | null>(null);
    const [selectedCall, setSelectedCall] = useState<CallRef | null>(null);
    const goBack = useCallback(() => setDrillKey(null), []);

    useEffect(() => { window.scrollTo({ top: 0 }); }, [drillKey]);

    useEffect(() => {
        setTopBarProps({
            breadcrumbs: drillKey ? "Reports › Voice Agent" : "Reports",
            title: drillKey ? DRILL_TITLES[drillKey] : "Voice Agent",
            dateFilters: { value: period, onChange: setPeriod },
            leftAddon: drillKey ? (
                <div onClick={goBack} style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginRight: 4 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
                </div>
            ) : null,
            onExport: () => {},
        });
    }, [setTopBarProps, drillKey, period, setPeriod, goBack]);

    return (
        <DesktopLayout>
            {api.loading
                ? <DesktopShimmer />
                : drillKey
                    ? <DrillContent drillKey={drillKey} data={data} summary={api.summary} locationId={api.locationId} from={api.from} to={api.to} period={period} isDesktop onOpenCall={setSelectedCall} />
                    : <MainDashboard data={data} summary={api.summary} topItems={api.topItems} topItemsLoading={api.loading} timeSaved={api.timeSaved} timeSavedLoading={api.loading} callVolumeByHour={api.callVolumeByHour} callVolumeByHourLoading={api.callVolumeByHourLoading} isDesktop onDrill={setDrillKey} onOpenCall={setSelectedCall} locationId={api.locationId} from={api.from} to={api.to} period={period} />}
            <TranscriptSlideOver call={selectedCall} onClose={() => setSelectedCall(null)} isDesktop />
        </DesktopLayout>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE VIEW
   ═══════════════════════════════════════════════════════════════════════════ */

function MobileView({ data, api, period, setPeriod }: {
    data: VoiceAgentData; api: VoiceAgentApiCtx; period: string; setPeriod: (p: string) => void;
}) {
    const [drillKey, setDrillKey] = useState<DrillKey | null>(null);
    const [selectedCall, setSelectedCall] = useState<CallRef | null>(null);
    const setTopBarProps = useDesktopTopBar();
    useDrillHistoryBack(!!drillKey, () => setDrillKey(null));

    useEffect(() => { window.scrollTo({ top: 0 }); }, [drillKey]);

    useEffect(() => {
        setTopBarProps({
            title: drillKey ? DRILL_TITLES[drillKey] : "Voice Agent",
            dateFilters: { value: period, onChange: setPeriod },
            onBack: drillKey ? () => setDrillKey(null) : undefined,
        });
    }, [setTopBarProps, drillKey, period, setPeriod]);

    return (
        <div style={{ width: "100%", minHeight: "100vh", background: T.bg }}>
            <TopBar variant="mobile" />
            {api.loading
                ? <MobileShimmer />
                : drillKey
                    ? <DrillContent drillKey={drillKey} data={data} summary={api.summary} locationId={api.locationId} from={api.from} to={api.to} period={period} isDesktop={false} onOpenCall={setSelectedCall} />
                    : <MainDashboard data={data} summary={api.summary} topItems={api.topItems} topItemsLoading={api.loading} timeSaved={api.timeSaved} timeSavedLoading={api.loading} callVolumeByHour={api.callVolumeByHour} callVolumeByHourLoading={api.callVolumeByHourLoading} isDesktop={false} onDrill={setDrillKey} onOpenCall={setSelectedCall} locationId={api.locationId} from={api.from} to={api.to} period={period} />}
            <TranscriptSlideOver call={selectedCall} onClose={() => setSelectedCall(null)} isDesktop={false} />
            <BottomNav />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENTRY POINT
   ═══════════════════════════════════════════════════════════════════════════ */

// Recursive fallback lookup — same shape as SnapshotScreen's findLocationId —
// for sessions where `selectedBranch` hasn't resolved an id/locationId field
// yet but `auth` carries one nested inside it.
const findLocationId = (obj: any, depth: any = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['locationId', 'location_id', 'locationID', 'locId', 'outletId', 'outlet_id']) {
        if (obj[key] && typeof obj[key] === 'string') return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

export default function VoiceAgentReport() {
    const isDesktop = useIsDesktop();
    const [period, setPeriod] = useSharedReportDateFilter("7D");
    const { data } = useVoiceAgentData(period);

    const { auth } = useAuth();
    const { selectedBranch } = useBranch();
    const locationId = useMemo(() => (selectedBranch as any)?.id ?? (selectedBranch as any)?.locationId ?? findLocationId(auth) ?? "", [selectedBranch, auth]);
    const range = useMemo(() => resolveDateRange(period), [period]);

    const { summary, topItems, timeSaved, loading } = useVoiceAgentApiData({ locationId, from: range.startDate, to: range.endDate, period });
    const { hours: callVolumeByHour, loading: callVolumeByHourLoading } = useCallVolumeByHour({ locationId, from: range.startDate, to: range.endDate, period });
    const api: VoiceAgentApiCtx = { locationId, from: range.startDate, to: range.endDate, summary, topItems, timeSaved, loading, callVolumeByHour, callVolumeByHourLoading };

    return isDesktop
        ? <DesktopView data={data} api={api} period={period} setPeriod={setPeriod} />
        : <MobileView data={data} api={api} period={period} setPeriod={setPeriod} />;
}
